/**
 * Formatação de datas/horas da plataforma.
 *
 * Toda a exibição usa o fuso oficial da congregação (America/Sao_Paulo),
 * independentemente do fuso do servidor (SSR roda em UTC) ou do navegador
 * do visitante. Nunca usar `toLocaleString`/`toLocaleDateString` direto nas
 * telas: sem `timeZone` o resultado muda entre servidor e cliente.
 */
export const APP_TIME_ZONE = "America/Sao_Paulo";

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export type DateInput = string | number | Date | null | undefined;

/**
 * Converte a entrada em `Date`. Datas sem hora (YYYY-MM-DD) são fixadas ao
 * meio-dia UTC para que o dia não "volte" ao converter para BRT.
 */
export function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const date =
    typeof value === "string"
      ? new Date(DATE_ONLY.test(value) ? `${value}T12:00:00Z` : value)
      : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function format(value: DateInput, options: Intl.DateTimeFormatOptions, fallback: string): string {
  const date = toDate(value);
  if (!date) return fallback;
  try {
    return new Intl.DateTimeFormat("pt-BR", { timeZone: APP_TIME_ZONE, ...options }).format(date);
  } catch {
    return fallback;
  }
}

/** Data + hora no fuso de São Paulo (ex.: 14/09/2026 10:58). */
export function formatDateTimeBR(
  value: DateInput,
  options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  },
  fallback = "—",
): string {
  return format(value, options, fallback);
}

/** Somente a data no fuso de São Paulo (ex.: 14/09/2026). */
export function formatDateBR(
  value: DateInput,
  options: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "numeric" },
  fallback = "—",
): string {
  return format(value, options, fallback);
}

/** Somente a hora no fuso de São Paulo (ex.: 10:58). */
export function formatTimeBR(
  value: DateInput,
  options: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" },
  fallback = "—",
): string {
  return format(value, options, fallback);
}

/** Data de "hoje" (YYYY-MM-DD) no fuso de São Paulo. */
export function todayInSaoPaulo(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}
